/**
 * ReworkStartModal - Confirmation modal to start a rework/reprint task
 *
 * When user clicks on a rework task in My Work, this modal asks:
 * "Do you want to start the reprint for [job]?"
 *
 * If Yes: Automatically starts PRINTS-MACH operation for that job
 */

import { useState } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function ReworkStartModal({
  isOpen,
  task,
  onClose,
  onStarted,
  token,
  employeeId,
  employeeName,
}) {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !task) return null;

  const handleStart = async () => {
    setIsStarting(true);
    setError(null);

    try {
      // Use job number as the job ID for the API
      const jobId = task.jobNumber;

      // Start the PRINTS-MACH operation for this job
      const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/time/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          jobNumber: task.jobNumber,
          operationCode: 'PRINTS-MACH', // Auto-start with PRINTS-MACH
          quoteNumber: task.quoteNumber,
          orderNumber: task.orderNumber,
          workCenter: 'OPTI',
          projectName: task.projectName,
          // Rework tracking fields
          reworkCause: task.issueType,
          reworkNotes: task.description,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to start reprint');
      }

      // Dispatch time-updated event to refresh sessions
      window.dispatchEvent(new Event('time-updated'));

      // Call success callback
      onStarted?.();
      onClose();
    } catch (err) {
      console.error('Error starting reprint:', err);
      setError(err.message || 'Failed to start reprint');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-400 to-orange-400">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Start Reprint
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="mb-4">
            <p className="text-gray-700 mb-3">
              You have a reprint task waiting. Would you like to start working on it now?
            </p>

            {/* Task Details */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-gray-900">
                  {task.jobNumber}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  task.severity === 'scrap'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {task.severity === 'scrap' ? 'SCRAP' : 'REPRINT'}
                </span>
              </div>

              {task.projectName && (
                <p className="text-sm text-gray-600 mb-2">{task.projectName}</p>
              )}

              <div className="flex flex-col gap-1 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Issue:</span>
                  <span>{task.issueLabel || task.issueType}</span>
                </div>
                {task.description && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium">Notes:</span>
                    <span className="text-gray-600">{task.description}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-medium">Reported by:</span>
                  <span>{task.reportedBy}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-3">
              Clicking <strong>Start Reprint</strong> will begin a PRINTS-MACH time ticket for this job.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isStarting}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleStart}
            disabled={isStarting}
            className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold rounded-lg shadow-md transition-all flex items-center gap-2"
          >
            {isStarting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Starting...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
                Start Reprint
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
